import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import Layout from './components/Layout';
import Home from './pages/Home';
import Promo from './pages/Promo';
import ContentPage from './pages/ContentPage';
import Gallery from './pages/Gallery';
import Contact from './pages/Contact';
import About from './pages/About';
import Committee from './pages/Committee';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import AdminPanel from './pages/AdminPanel';
import AuthCallback from './pages/AuthCallback';
import DynamicPage from './pages/DynamicPage';
import MenuGroupOverview from './pages/MenuGroupOverview';
import ConductMeeting from './pages/ConductMeeting';
import InvoicePay from './pages/InvoicePay';
import PageBuilder from './pages/PageBuilder';
import SystemTemplates from './pages/SystemTemplates';
import MembersDirectory from './pages/MembersDirectory';
import MembersPhotos from './pages/MembersPhotos';
import PhotoAlbum from './pages/PhotoAlbum';
import MyActions from './pages/MyActions';
import Events from './pages/Events';
import Meetings from './pages/Meetings';
import MeetingDetail from './pages/MeetingDetail';
import Governance from './pages/Governance';
import Shop from './pages/Shop';
import ShopSuccess from './pages/ShopSuccess';
import RafflePage from './pages/RafflePage';
import RaffleClaim from './pages/RaffleClaim';
import TicketScan from './pages/TicketScan';
import StubScan from './pages/StubScan';
import MyTicketBooks from './pages/MyTicketBooks';
import ContactEditPage from './pages/ContactEditPage';
import PhotoBookPage from './pages/PhotoBookPage';
import Memorabilia from './pages/Memorabilia';
import Subgroups from './pages/Subgroups';
import SubgroupHome from './pages/SubgroupHome';
import SubgroupPage from './pages/SubgroupPage';
import SubgroupManage from './pages/SubgroupManage';
import GoogleCalendar from './pages/GoogleCalendar';
import DocumentsLibrary from './pages/DocumentsLibrary';
import MemberProfile from './pages/MemberProfile';
import Support from './pages/Support';
import PitchCalculator from './pages/PitchCalculator';
import MembershipApply from './pages/MembershipApply';
import ShortLink from './pages/ShortLink';
import VerifyEmail from './pages/VerifyEmail';
import ProductDetail from './pages/ProductDetail';
import MyAccount from './pages/MyAccount';
import MyDetails from './pages/MyDetails';
import { PaymentSuccess, PaymentCancel } from './pages/PaymentResult';
import ThemeApplier from './components/ThemeApplier';
import { Toaster } from 'sonner';
import PageTracker from './components/PageTracker';
import PlaceholderDoubleClick from './components/PlaceholderDoubleClick';
import { NAV_ITEMS } from './mock';

// Build routes for the generic content pages (skip ones with custom components)
const CUSTOM = new Set(['home', 'publications-gallery', 'publications-slideshow', 'contact', 'members-directory', 'members-photos', 'memorabilia', 'events', 'shop', 'subgroups', 'meetings', 'governance', 'members-apply']);
const contentRoutes = [];
NAV_ITEMS.forEach((item) => {
  const handle = (node) => {
    if (!node.path || CUSTOM.has(node.pageKey)) return;
    contentRoutes.push({ path: node.path, pageKey: node.pageKey });
  };
  if (item.children) item.children.forEach(handle);
  else handle(item);
});

function AppRoutes() {
  const location = useLocation();
  // Handle OAuth return synchronously (before any protected route runs)
  if (location.hash && location.hash.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Layout>
      <PageTracker />
      <PlaceholderDoubleClick />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/promo" element={<Promo />} />
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email/:token" element={<VerifyEmail />} />
        <Route path="/admin" element={<AdminPanel />} />
        <Route path="/admin/pages/builder/:pageKey" element={<PageBuilder />} />
        <Route path="/admin/templates" element={<SystemTemplates />} />
        <Route path="/admin/meetings/:id/conduct" element={<ConductMeeting />} />
        <Route path="/admin/:section" element={<AdminPanel />} />
        <Route path="/publications/gallery" element={<Gallery pageKey="publications-gallery" mode="grid" />} />
        <Route path="/publications/slideshow" element={<Gallery pageKey="publications-slideshow" mode="slideshow" />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/about" element={<About />} />
        <Route path="/committee" element={<Committee />} />
        <Route path="/support" element={<Support />} />
        <Route path="/pitch/calculator" element={<PitchCalculator />} />
        <Route path="/members/directory" element={<MembersDirectory />} />
        <Route path="/members/photos" element={<MembersPhotos />} />
        <Route path="/members/photos/album/:name" element={<PhotoAlbum />} />
        <Route path="/memorabilia" element={<Memorabilia />} />
        <Route path="/members/actions" element={<MyActions />} />
        <Route path="/events" element={<Events />} />
        <Route path="/meetings" element={<Meetings />} />
        <Route path="/meetings/:id" element={<MeetingDetail />} />
        <Route path="/governance" element={<Governance />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/shop/success" element={<ShopSuccess />} />
        <Route path="/raffle/:id" element={<RafflePage />} />
        <Route path="/r/:id" element={<RafflePage />} />
        <Route path="/p/:productId" element={<ProductDetail />} />
        <Route path="/raffle/:id/claim" element={<RaffleClaim />} />
        <Route path="/t/:code" element={<TicketScan />} />
        <Route path="/s/:code" element={<StubScan />} />
        <Route path="/my-ticket-books" element={<MyTicketBooks />} />
        <Route path="/admin/contacts/new" element={<ContactEditPage />} />
        <Route path="/admin/contacts/:userId" element={<ContactEditPage />} />
        <Route path="/photobook/:id" element={<PhotoBookPage />} />
        <Route path="/shop/:productId" element={<ProductDetail />} />
        <Route path="/members/apply" element={<MembershipApply />} />
        <Route path="/l/:code" element={<ShortLink />} />
        <Route path="/groups" element={<Subgroups />} />
        <Route path="/g/:slug" element={<SubgroupHome />} />
        <Route path="/g/:slug/manage" element={<SubgroupManage />} />
        <Route path="/g/:slug/:pageKey" element={<SubgroupPage />} />
        <Route path="/calendar" element={<GoogleCalendar />} />
        <Route path="/documents" element={<DocumentsLibrary />} />
        <Route path="/members/directory/:memberId" element={<MemberProfile />} />
        <Route path="/account" element={<MyAccount />} />
        <Route path="/my-details" element={<MyDetails />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/pay/invoice/:id" element={<InvoicePay />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/p/:slug" element={<DynamicPage />} />
        <Route path="/menu/:slug" element={<MenuGroupOverview />} />
        {contentRoutes.map((r) => (
          <Route key={r.path} path={r.path} element={<ContentPage pageKey={r.pageKey} />} />
        ))}
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ThemeApplier />
          <Toaster richColors position="top-center" />
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
